import posthog from 'posthog-js'
import type { StateCreator } from "zustand"
import { trackEvent, setUserProperties, setUserPropertiesOnce } from '@/utils/analytics'
import type { loadingState } from "./loadingSlice"
import type { UserRole } from '@/types/users'
import { apiService } from '@/service/api'
import {
  keycloak,
  initKeycloak,
  login as keycloakLogin,
  loginWithGoogle as keycloakLoginWithGoogle,
  loginWithMicrosoft as keycloakLoginWithMicrosoft,
  register as keycloakRegister,
  logout as keycloakLogout,
  getToken,
  getUserInfo,
  ensureFreshToken,
  forceTokenRefresh,
  switchOrganization as keycloakSwitchOrganization,
  isKeycloakInitialized,
  setAuthStateChangeCallback,
  type KeycloakUserInfo,
  type OrganizationInfo,
} from "@/config/keycloakClient"

export type User = {
  userId: string
  email: string
  name: string
  given_name: string
  family_name: string
  /**
   * The avatar to RENDER — an uploaded photo when the user has one, otherwise
   * the identity provider's (FS-693).
   *
   * Every avatar surface already reads this one field (the left rail,
   * `ProfilePicture` in both settings pages, chat messages, onboarding,
   * `Navigation`), so resolving precedence here is what makes an upload appear
   * in all of them without touching any of them.
   */
  picture?: string
  /**
   * The identity provider's photo, unresolved.
   *
   * Kept separately so `picture` can be overwritten with an upload without
   * losing what SSO supplied — removing an uploaded photo reverts to this, and
   * the profile UI uses it to say the current photo came from Google/Microsoft.
   */
  ssoPicture?: string
  role?: UserRole
  tenant_id?: string
  isInternalOrg?: boolean
}

export interface authSessionState {
  user: User | null
  userLogged: boolean
  tenantId: string
  /** All organizations (tenants) the user is a member of, from the JWT. */
  organizations: OrganizationInfo[]
  /** The currently active organization id (= tenantId). */
  activeOrganizationId: string | null
  /** True while a tenant switch is in flight (for UI loading state). */
  switchingTenant: boolean
  accessToken: string | null
  keycloakInitialized: boolean
  /**
   * Object URL of the user's UPLOADED avatar, or null when they have none
   * (FS-693).
   *
   * Held in slice state rather than only on `user.picture` because every token
   * refresh rebuilds `user` from the token — so without somewhere durable to
   * re-read it from, an uploaded photo would revert to the SSO one the first
   * time the token refreshed.
   */
  uploadedAvatarUrl: string | null
  /** Fetch the uploaded avatar once per session (no-op if already loaded). */
  loadUploadedAvatar: () => Promise<void>
  /**
   * Backfill the SSO (Google) photo when the token has no `picture` claim.
   *
   * `picture` reaches us from `token.picture` (keycloakClient), which is only
   * populated if the claim is mapped in Keycloak. When it is not, the photo is
   * NOT lost — OrgSyncInterceptor writes the claim into
   * `organization_members.picture` on every authenticated request, and
   * `GET /v1/organization/users` returns it for every member including
   * yourself. Without this, every surface that reads `user.picture` (the left
   * rail, the top bar, chat, Account Details) showed an initial while the
   * photo sat in our own database.
   *
   * No-ops when a photo is already resolved, so it costs nothing for users
   * whose claim IS mapped.
   */
  resolveSsoPicture: () => Promise<void>
  /**
   * Apply a just-uploaded photo, or clear it. `null` reverts `picture` to the
   * identity provider's photo, which is what "Remove photo" means.
   */
  setUploadedAvatar: (blob: Blob | null) => void
  logout: () => Promise<void>
  login: (redirectUri?: string) => void
  loginWithGoogle: (redirectUri?: string) => void
  loginWithMicrosoft: (redirectUri?: string) => void
  register: (redirectUri?: string) => void
  verifyUserSession: () => Promise<void>
  refreshUserSession: (force?: boolean) => Promise<void>
  initializeAuth: () => Promise<boolean>
  /**
   * Switch the active tenant. No-op if already active. Reloads onto the
   * tenant-neutral homepage on success, unless `reload: false`: then the new
   * token is applied in place and the caller decides where to go.
   */
  switchTenant: (organizationId: string, options?: { reload?: boolean }) => Promise<void>
}

export const initialAuthSessionState = {
  user: null,
  userLogged: false,
  tenantId: '',
  organizations: [] as OrganizationInfo[],
  activeOrganizationId: null as string | null,
  switchingTenant: false,
  accessToken: null,
  keycloakInitialized: false,
  uploadedAvatarUrl: null as string | null,
}

/**
 * `uploadedAvatarUrl` is threaded in rather than read from the token, because
 * the token has no idea about it: the upload lives in our own database. Every
 * caller passes the current value so a rebuild (token refresh, tenant switch)
 * re-applies it instead of silently reverting to the SSO photo.
 */
const mapKeycloakUserToUser = (
  keycloakUser: KeycloakUserInfo,
  uploadedAvatarUrl: string | null,
): User => ({
  userId: keycloakUser.userId,
  email: keycloakUser.email,
  name: keycloakUser.name,
  given_name: keycloakUser.given_name,
  family_name: keycloakUser.family_name,
  picture: uploadedAvatarUrl ?? keycloakUser.picture,
  ssoPicture: keycloakUser.picture,
  role: keycloakUser.role,
  tenant_id: keycloakUser.tenant_id,
  isInternalOrg: keycloakUser.isInternalOrg,
})

const updateAuthState = (
  set: (partial: Partial<authSessionState>) => void,
  userInfo: KeycloakUserInfo | null,
  token: string | null,
  uploadedAvatarUrl: string | null = null
) => {
  if (userInfo) {
    posthog.identify(userInfo.userId, {
      email: userInfo.email,
      name: userInfo.name,
      tenant_id: userInfo.tenant_id,
    })
    if (userInfo.tenant_id) {
      posthog.group('tenant', userInfo.tenant_id)
    }
    // Set user properties for retention/segmentation analysis.
    // $set_once properties (like signup_date) are only written on first identify.
    setUserProperties({
      user_role: userInfo.role,
      has_tenant: !!userInfo.tenant_id,
      is_internal_org: userInfo.isInternalOrg ?? false,
    })
    posthog.setPersonPropertiesForFlags({
      tenant_id: userInfo.tenant_id,
      user_role: userInfo.role,
    })
    set({
      userLogged: true,
      user: mapKeycloakUserToUser(userInfo, uploadedAvatarUrl),
      accessToken: token,
      tenantId: userInfo.tenant_id || '',
      organizations: userInfo.organizations,
      activeOrganizationId: userInfo.activeOrganizationId || null,
    })
  } else {
    set({
      userLogged: false,
      user: null,
      accessToken: null,
      tenantId: '',
      organizations: [],
      activeOrganizationId: null,
    })
  }
}

const clearAuthState = (set: (partial: Partial<authSessionState>) => void) => {
  set({
    userLogged: false,
    user: null,
    accessToken: null,
    tenantId: '',
    organizations: [],
    activeOrganizationId: null,
    uploadedAvatarUrl: null,
  })
}

export const authSessionSlice: StateCreator<
  authSessionState & loadingState,
  [],
  [],
  authSessionState
> = (set, get) => ({
  ...initialAuthSessionState,

  /**
   * Load the uploaded avatar, once.
   *
   * A 404 from the endpoint means "this user has not uploaded one", which is
   * the common case and not an error — `picture` then keeps the SSO photo. A
   * genuine failure is swallowed for the same reason: a missing avatar must
   * never be the thing that stops the app booting.
   */
  loadUploadedAvatar: async () => {
    if (get().uploadedAvatarUrl) return
    try {
      const blob = await apiService.getMyAvatar()
      if (blob) get().setUploadedAvatar(blob)
    } catch {
      // Leave the SSO photo (or the initial) in place.
    }
  },

  resolveSsoPicture: async () => {
    const { user, uploadedAvatarUrl } = get()
    // An upload always wins, and a present claim needs no help.
    if (!user || uploadedAvatarUrl || user.picture) return
    try {
      const data = await apiService.getOrganizationUsers()
      const me = data.users.find((m) => m.user_id === user.userId)
      const picture = me?.picture
      if (!picture) return
      const current = get().user
      // Re-checked because `loadUploadedAvatar` runs concurrently: if it landed
      // first, its blob URL is the answer and must not be overwritten.
      if (!current || current.picture) return
      // `ssoPicture` too, so "Remove photo" reverts to the Google photo rather
      // than to nothing — setUploadedAvatar(null) reads exactly that field.
      set({ user: { ...current, picture, ssoPicture: picture } })
    } catch {
      // The initial on the gradient is a correct fallback; never block boot.
    }
  },

  setUploadedAvatar: (blob: Blob | null) => {
    const { uploadedAvatarUrl, user } = get()
    // Revoke the one being replaced, or the browser holds every blob this
    // session ever created.
    if (uploadedAvatarUrl) URL.revokeObjectURL(uploadedAvatarUrl)
    const nextUrl = blob ? URL.createObjectURL(blob) : null
    set({
      uploadedAvatarUrl: nextUrl,
      // `?? ssoPicture` is what makes "Remove photo" mean "go back to my
      // Google photo" rather than "go blank".
      ...(user ? { user: { ...user, picture: nextUrl ?? user.ssoPicture } } : {}),
    })
  },

  initializeAuth: async () => {
    setAuthStateChangeCallback((authenticated: boolean) => {
      if (authenticated) {
        updateAuthState(set, getUserInfo(), getToken(), get().uploadedAvatarUrl)
      } else {
        // Session expired or admin-forced logout: reset PostHog to prevent
        // identity merging if a different user logs in on the same browser
        posthog.reset()
        clearAuthState(set)
      }
    })
    
    // Use module state as source of truth - initKeycloak() is idempotent
    if (isKeycloakInitialized()) {
      set({ keycloakInitialized: true })
      if (keycloak.authenticated) {
        updateAuthState(set, getUserInfo(), getToken(), get().uploadedAvatarUrl)
        void get().loadUploadedAvatar()
        void get().resolveSsoPicture()
      } else {
        clearAuthState(set)
      }
      return keycloak.authenticated || false
    }
    
    try {
      const authenticated = await initKeycloak()
      set({ keycloakInitialized: true })

      if (authenticated) {
        updateAuthState(set, getUserInfo(), getToken(), get().uploadedAvatarUrl)
        // Boot only — deliberately not on the token-refresh paths, which run
        // often and would re-fetch an image that has not changed.
        void get().loadUploadedAvatar()
        void get().resolveSsoPicture()
        const loginMethod = sessionStorage.getItem('posthog_login_method')
        if (loginMethod) {
          sessionStorage.removeItem('posthog_login_method')
          trackEvent('auth-login_succeeded', { auth_method: loginMethod })
          // Set signup_date only on first-ever identify (won't overwrite if already set)
          setUserPropertiesOnce({ signup_date: new Date().toISOString() })
        }
      } else {
        clearAuthState(set)
      }
      
      return authenticated
    } catch (error) {
      console.error('Failed to initialize Keycloak:', error)
      clearAuthState(set)
      return false
    }
  },

  login: (redirectUri?: string) => {
    sessionStorage.setItem('posthog_login_method', 'keycloak')
    keycloakLogin(redirectUri)
  },

  loginWithGoogle: (redirectUri?: string) => {
    sessionStorage.setItem('posthog_login_method', 'google')
    keycloakLoginWithGoogle(redirectUri)
  },

  loginWithMicrosoft: (redirectUri?: string) => {
    sessionStorage.setItem('posthog_login_method', 'microsoft')
    keycloakLoginWithMicrosoft(redirectUri)
  },

  register: (redirectUri?: string) => {
    sessionStorage.setItem('posthog_login_method', 'register')
    keycloakRegister(redirectUri)
  },

  logout: async () => {
    trackEvent('auth-logout_completed', {}, { send_instantly: true, transport: 'sendBeacon' })
    posthog.reset()
    clearAuthState(set)
    keycloakLogout()
  },

  verifyUserSession: async () => {
    const { userLogged } = get()
    
    if (userLogged) return
    
    if (!isKeycloakInitialized()) {
      await get().initializeAuth()
      return
    }
    
    if (keycloak.authenticated) {
      updateAuthState(set, getUserInfo(), getToken(), get().uploadedAvatarUrl)
    } else {
      clearAuthState(set)
    }
  },

  refreshUserSession: async (force: boolean = true) => {
    const newToken = force ? await forceTokenRefresh() : await ensureFreshToken()

    if (newToken) {
      updateAuthState(set, getUserInfo(), newToken, get().uploadedAvatarUrl)
    } else {
      throw new Error('Token refresh failed')
    }
  },

  switchTenant: async (organizationId: string, options?: { reload?: boolean }) => {
    const { activeOrganizationId, organizations, switchingTenant } = get()

    // No-op if already on this tenant or mid-switch.
    if (switchingTenant || organizationId === activeOrganizationId) {
      return
    }
    // Guard: only allow switching to an org the user is actually a member of.
    // (The server also enforces this, but failing fast avoids a wasted round-trip.)
    if (!organizations.some((o) => o.id === organizationId)) {
      throw new Error('Cannot switch to an organization the user is not a member of')
    }

    set({ switchingTenant: true })
    try {
      const newToken = await keycloakSwitchOrganization(organizationId)
      if (!newToken) {
        throw new Error('Tenant switch failed: no token returned')
      }

      // Reflect the new active tenant in the store before navigating so any
      // synchronous reads (and analytics below) see the switched org.
      updateAuthState(set, getUserInfo(), newToken, get().uploadedAvatarUrl)
      trackEvent('tenant-switched', { tenant_id: organizationId })

      // An org link (`?org=`, see utils/orgLink) switches before its page has
      // mounted, so there is no tenant-A data to drop: skip the reload, which
      // would cost a second full app load (and, when the silent-SSO check
      // fails, a second login).
      if (options?.reload === false) {
        set({ switchingTenant: false })
        return
      }

      // Navigate to the tenant-neutral landing route AND fully reload. The URL
      // change is required because the current route may be tenant-A-scoped
      // (e.g. /boards/<id>, /chat/<id>) and would 404/403 under tenant B.
      // The reload is required because the store uses only `devtools` (no
      // `persist`), its 19 slices have no central reset hook, and a fresh page
      // load is the safe way to drop all in-memory tenant-A data (boards,
      // docs, chats, caches) before the tenant-B view mounts.
      // `window.location.assign('/intelligent-board')` does both (navigate +
      // reload) in one shot. `/intelligent-board` is the product's homepage —
      // a tenant-neutral landing surface that re-fetches its own data on mount.
      window.location.assign('/intelligent-board')
    } catch (error) {
      set({ switchingTenant: false })
      throw error
    }
  },
})
